'use strict'

import { axiosPost, axiosGet, UPLOAD_CONFIG } from '../axios/axios.js'
import loadImage from 'blueimp-load-image'
import piexif from 'piexifjs'
import moment from 'moment'
import 'moment-precise-range-plugin'

export function getPhoneHref (phone, type = 'tel') {
  const phoneNumber = formatPhoneNumber(phone)
  if (phoneNumber) {
    if (type === 'tel') {
      return 'tel:8' + phoneNumber
    } else if (type === 'wa') {
      return 'https://wa.me/7' + phoneNumber
    } else if (type === 'tg') {
      return 'https://t.me/+7' + phoneNumber
    }
  }
  return null
}

export function formatPhoneNumber (phone) {
  if (phone != null && typeof phone === 'string' && phone.length) {
    phone = phone.replace(/\D+/g, '')
    if (phone.length === 11) { // 7... или 8...
      phone = phone.substring(1)
    } else if (phone.length !== 10) {
      return null
    }
    return phone
  }
  return null
}

const ovimContractorUserLogins = [
  'skasdDisp',
  'skasdBrig',
  'sinapsDisp',
  'sinapsBrig',
  'sinergiyaDisp',
  'sinergiyaBrig',
  'technokomDisp',
  'technokomBrig',
  'nmksistemaDisp',
  'nmksistemaBrig',
  'stroikomDisp',
  'stroikomBrig',
  'rikomDisp',
  'rikomBrig',
  'galcevDisp',
  'galcevBrig',
  'pskkontaktDisp',
  'pskkontaktBrig',
  'rosaDisp',
  'rosaBrig'
]

export function isOvimContractor (login) {
  return ovimContractorUserLogins.includes(login)
}

export function prepareUser (user) {
  const employee = user.employee
  const login = user.login

  if (employee) {
    user.role = employee.role
    user.roleName = employee.roleName
    user.roles = employee.roles || {}
    user.imageId = employee.imageId
    user.isContractor = !!employee.isContractor
    user.isTitan = login === 'titanDisp' || login.startsWith('pTitan')
    user.isTitan100 = login === 'pTitan1'
    user.isOvimContractor = isOvimContractor(login)
    user.isMGSS = login === 'mgssDisp' || login === 'pMgss' || login === 'komkorDisp' || login === 'komkorBrig'
    user.isRSG = login === 'rsgDisp' || login === 'pRsg' || login.startsWith('Rsg-')
    user.isAvtostradaPlus = login === 'avtostradaPlusDisp' || login === 'avtostradaPlus'
    user.isMikhail = login === 'mikhailDisp' || login === 'mikhail'

    user.isTransport = employee.workAreas && employee.workAreas.length === 1 && employee.workAreas[0] === 'Транспорт'
    user.isMedicalTablo = !!user.roles.viewMedicalTablo && !user.roles.medicalAdmin
    user.isMedic = !user.isMedicalTablo && !!user.roles.medical && !user.roles.medicalAdmin && !user.roles.transportAdmin

    user.externalCurator = !!user.systemUser.roles.includes('externalCurator')
    user.restrictedAccess = !!employee.restrictedAccess
  } else {
    const systemUser = user.systemUser

    if (systemUser.root) {
      user.role = 'root'
      user.roleName = 'Полный доступ'
      user.roles = { root: 'Полный доступ' }
    } else {
      if (systemUser.fullaccess) {
        user.role = 'admin'
        user.roleName = 'Администратор'
        user.roles = { admin: 'Администратор' }
      } else {
        user.role = 'deltaUser'
        user.roleName = 'Пользователь'
        user.roles = { deltaUser: 'Пользователь' }
      }
    }
    user.isContractor = false
    user.isTitan = false
    user.isTitan100 = false
    user.isOvimContractor = false
    user.isMGSS = false
    user.isRSG = false
    user.isAvtostradaPlus = false
    user.isMikhail = false

    user.isTransport = false
    user.isMedic = false
    user.isMedicalTablo = false

    user.externalCurator = false
    user.restrictedAccess = false
  }

  user.fullAccess = !!(user.roles.root || (user.roles.admin && user.systemUser.fullaccess))
  user.hasOperation = function (operation) {
    if (!this.roles) {
      return false
    } else if (this.roles.root) {
      return true
    }
    return !!this.systemUser && this.systemUser.operations.includes(operation)
  }

  // Проверка ролей БП
  user.hasRole = function (...roles) {
    // this.hasSystemRole(roles)
    if (!this.roles) {
      return false
    } else if (this.roles.root) {
      return true
    }
    return this.hasRoleStrict(...roles)
  }

  user.hasRoleStrict = function (...roles) {
    if (this.roles) {
      for (let i = 0; i < roles.length; i++) {
        if (this.roles[roles[i]]) {
          return true
        }
      }
    }
    return false
  }

  // Проверка системных ролей (включают в себя роли БП)
  user.hasSystemRole = function (...roles) {
    if (!this.roles) {
      return false
    } else if (this.roles.root) {
      return true
    }
    return this.hasSystemRoleStrict(...roles)
  }

  user.hasSystemRoleStrict = function (...roles) {
    if (this.roles && this.systemUser) {
      for (let i = 0; i < roles.length; i++) {
        if (this.systemUser.roles.includes(roles[i])) {
          return true
        }
      }
    }
    return false
  }

  user.hasAccessRestriction = function (key) {
    const employee = this.employee
    if (employee && employee.restrictedAccess) {
      if (employee.restrictedAccessConfiguration) {
        return employee.restrictedAccessConfiguration[key]
      }
      return true
    }
    return false
  }

  const restrictedExternalUser = user.restrictedAccess && user.employee && user.employee.external
  const restrictedExternalDispatcher = restrictedExternalUser && user.hasRole('dispatcher')
  user.strictRestrictedAccess = user.hasAccessRestriction('fullStrict') && !restrictedExternalDispatcher

  user.hasUIAccess = function (featureKey) {
    switch (featureKey) {
      case 'displayRequestPopup':
      case 'displayOpenRequestBtn':
        // TODO по доступу к данным заявки
        if (this.hasSystemRoleStrict('publicationRead') || this.strictRestrictedAccess) { // TODO hasAccessRestriction('request')
          return false
        }
        break
      case 'displayServiceObjectPopup':
        // TODO по доступу к данным объекта обслуживания
        if (this.isMGSS || this.isRSG || this.hasSystemRoleStrict('publicationRead') || this.strictRestrictedAccess) {
          return false
        }
        break
      case 'displayUserPopupFullMenu':
        if (this.hasSystemRoleStrict('publicationRead') || this.restrictedAccess || this.hasSystemRoleStrict('krotos')) {
          return false
        }
        break
      case 'serviceObject.listExport':
        return this.hasOperation('build report') && !(this.isMGSS || this.isRSG) && !this.restrictedAccess
      case 'serviceObject.displayInternalData':
      case 'report.displayInternalData':
        return !user.hasSystemRoleStrict('publicationRead') && !user.restrictedAccess
      case 'report.listExport':
        return user.hasOperation('build report') && !user.strictRestrictedAccess
      case 'serviceObject.maintenanceNote.edit':
        return !this.restrictedAccess
      case 'request.displayInternalData':
        return !restrictedExternalUser
      default:
        break
    }
    return true
  }

  return user
}

export function prepareProposedBrigades (proposedBrigades) {
  for (let i = 0; i < proposedBrigades.length; i++) {
    // Подготовка поля с невыполняемыми условиям бригады.
    let atLeastOneCondition = false
    proposedBrigades[i].conditionsStr = ''
    for (const key in proposedBrigades[i].conditions) {
      if (proposedBrigades[i].conditions.hasOwnProperty(key)) {
        atLeastOneCondition = true
        proposedBrigades[i].conditionsStr += proposedBrigades[i].conditions[key].name + ', '
      }
    }

    if (atLeastOneCondition) {
      const conditionsStrLength = proposedBrigades[i].conditionsStr.length
      proposedBrigades[i].conditionsStr = proposedBrigades[i].conditionsStr.substring(0, conditionsStrLength - 2)
      proposedBrigades[i].conditionsStr += '.'
    }

    // Подготовка поля с составом бригады.
    proposedBrigades[i].employeesStr = ''
    for (let j = 0; j < this.proposedBrigades[i].employees.length; j++) {
      const employee = this.proposedBrigades[i].employees[j]
      proposedBrigades[i].employeesStr += employee.fio + '('

      if (employee.brigadier == true) {
        proposedBrigades[i].employeesStr += 'Бригадир, '
      }

      let atLeastOneContact = false
      for (let k = 0; k < employee.contacts.length; k++) {
        if (employee.contacts[k] != '') {
          atLeastOneContact = true
          proposedBrigades[i].employeesStr += employee.contacts[k] + ', '
        }
      }

      if (atLeastOneContact) {
        const contactsStrLength = proposedBrigades[i].employeesStr.length
        proposedBrigades[i].employeesStr = proposedBrigades[i].employeesStr.substring(0, contactsStrLength - 2)
      }

      if (j != (proposedBrigades[i].employees.length - 1)) {
        proposedBrigades[i].employeesStr += '), '
      } else {
        proposedBrigades[i].employeesStr += ').'
      }
    }
  }

  return proposedBrigades
}

export function parseDate (timeStr) {
  // формат timeStr = 'DD.MM.YYYY HH:MM'
  const pattern = /(\d{2}).(\d{2}).(\d{4})\s(\d{2}):(\d{2})/
  return new Date(timeStr.replace(pattern, '$3-$2-$1T$4:$5'))
}

export function parseOnlyDate (dateStr) {
  // формат dateStr = 'DD.MM.YYYY'
  const pattern = /(\d{2}).(\d{2}).(\d{4})/
  return new Date(dateStr.replace(pattern, '$3-$2-$1T00:00:00'))
}

export function convertTime (unixTimestamp) {
  return convertConfiguredTime(unixTimestamp)
}

export function convertConfiguredTime (unixTimestamp, config) {
  if (unixTimestamp == null) {
    return 'Нет данных'
  }
  return formatConfiguredTime(unixTimestamp, config) + ' '
}

export function formatTime (unixTimestamp) {
  return formatConfiguredTime(unixTimestamp)
}

export function formatDate (unixTimestamp) {
  return formatConfiguredTime(unixTimestamp, { year: true, month: true, date: true, hour: false, min: false, sec: false })
}

export function formatConfiguredTime (unixTimestamp, config = { year: true, month: true, date: true, hour: true, min: true, sec: true }) {
  if (unixTimestamp == null) {
    return null
  }

  let result = ''

  const a = new Date(unixTimestamp)
  const year = a.getFullYear()
  const month = ('0' + (a.getMonth() + 1)).slice(-2)
  const date = ('0' + a.getDate()).slice(-2)
  const hour = ('0' + a.getHours()).slice(-2)
  const min = ('0' + a.getMinutes()).slice(-2)
  const sec = ('0' + a.getSeconds()).slice(-2)

  if (config.date) {
    result += date
  }
  if (config.month) {
    result += '.' + month
  }
  if (config.year) {
    result += '.' + year
  }
  if (config.hour) {
    result += ' ' + hour
  }
  if (config.min) {
    result += ':' + min
  }
  if (config.sec) {
    result += ':' + sec
  }
  return result
}

export function convertIntervalToDeltaDH (value1, value2) {
  if (value1 == null || value2 == null) {
    return null
  }

  let diff
  if (value2 <= value1) {
    diff = value1 - value2
  } else {
    diff = value2 - value1
  }
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  if (days) {
    return days + ' д'
  }
  const hours = Math.floor(diff / (1000 * 60 * 60) - days * 24)
  if (hours) {
    return hours + ' ч'
  }
  return 'менее часа'
}

export function convertIntervalToYYMMDD (startMs, endMs) {
  if (!startMs || !endMs) {
    return null
  }

  const objectYYMMDD = moment.preciseDiff(moment(startMs), moment(endMs), true)

  return (objectYYMMDD.years > 0 ? objectYYMMDD.years + ' ' + slavicPlural(objectYYMMDD.years, 'г.', 'г.', 'л.') + ' ' : '') + (objectYYMMDD.months > 0 ? objectYYMMDD.months + ' мес. ' : '') + (objectYYMMDD.days > 0 ? objectYYMMDD.days + ' дн.' : '')
}

export function convertMSToDHMS (ms) {
  if (!ms) {
    return ms
  }

  let seconds = ms / 1000
  const days = parseInt(seconds / 86400)
  seconds = seconds % 86400
  const hours = parseInt(seconds / 3600)
  seconds = seconds % 3600
  const minutes = parseInt(seconds / 60)
  seconds = seconds % 60
  seconds = Math.round(seconds)

  return (days > 0 ? days + 'д.' : '') + (hours > 0 ? hours + 'ч.' : '') + (minutes > 0 ? minutes + 'мин.' : '') + (days <= 0 && hours <= 0 && minutes < 3 ? seconds + 'сек.' : '')
}

export function convertMSToYMD (ms) {
  const msInDay = 1000 * 60 * 60 * 24
  const msInMonth = msInDay * 30.44 // Approximate average month length
  const msInYear = msInDay * 365.25 // Approximate average year length

  const years = Math.floor(ms / msInYear)
  ms %= msInYear

  const months = Math.floor(ms / msInMonth)
  ms %= msInMonth

  const days = Math.floor(ms / msInDay)

  const yearsAlias = slavicPlural(years, 'г. ', 'г. ', 'л. ')

  return (years > 0 ? years + yearsAlias : '') +
         (months > 0 ? months + 'мес. ' : '') +
         (days > 0 ? days + 'д.' : '')
}

export function convertMSToDDHHMMSS (ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${days < 10 ? `0${days}` : days}:${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
}

export function convertDDHHMMSStoMS (ddhhmmss) {
  const parts = ddhhmmss.split(':').map(Number)
  if (parts.length !== 4) {
    throw new Error('Неверный формат времени. Ожидается ДД:ЧЧ:ММ:СС')
  }
  const [days, hours, minutes, seconds] = parts
  return (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000
}

export function sortItems (items, sortBy, descending) {
  return items.sort((aItem, bItem) => {
    const aValue = aItem[sortBy]
    const bValue = bItem[sortBy]

    let index
    if (aValue == undefined || aValue === '' || aValue == null) {
      index = -1
    } else if (bValue == undefined || bValue === '' || bValue == null) {
      index = 1
    } else if (typeof aValue === 'string') {
      if (!isNaN(aValue)) {
        index = aValue.localeCompare(bValue)
      } else {
        index = bValue.localeCompare(aValue)
      }
    } else if (Array.isArray(aValue) && typeof aValue[0] === 'number') {
      let aValueSum = 0
      let bValueSum = 0
      for (let i = 0; i < aValue.length; i++) {
        aValueSum = aValueSum + aValue[i]
      }
      for (let i = 0; i < bValue.length; i++) {
        bValueSum = bValueSum + bValue[i]
      }
      index = aValueSum == bValueSum ? 0 : aValueSum > bValueSum ? 1 : -1
    } else {
      index = aValue == bValue ? 0 : aValue > bValue ? 1 : -1
    }

    return descending ? -index : index
  })
}

export function filterData (items, text, searchColumns, searchMode = 'containsAll') {
  const searchText = text.replace(/\s+/g, ' ').trim().toLowerCase()

  return items.filter(item => {
    for (const searchColumn of searchColumns) {
      let value
      if (typeof searchColumn === 'function') {
        value = searchColumn(item)
      } else {
        value = item[searchColumn]
      }

      let lowerValue = null
      if (value != null) {
        if (typeof value === 'object') {
          lowerValue = value.name ? value.name.toString().toLowerCase() : null
        } else {
          lowerValue = value.toString().toLowerCase()
        }
      }

      if (lowerValue) {
        lowerValue = lowerValue.replace(/\s+/g, ' ').trim()
        if (searchMode === 'containsAll') {
          if (lowerValue.includes(searchText)) {
            return true
          }
        } else if (searchMode === 'containsAny') {
          const searchWords = searchText.toLowerCase().split(' ')
          if (searchWords.some(word => lowerValue.includes(word))) {
            return true
          }
        }
      }
    }
    return false
  })
}

export function hasNotEmptyItems (items) {
  return !!items && items.length !== 0 && items.find(item => item != null) != null
}

export function prepareFilesDataToUpload () {
  let objectId, preloadFormId, files, categoryKey, url, documentType, property
  if (arguments.length === 1 && typeof arguments[0] === 'object') {
    const config = arguments[0]
    objectId = config.objectId
    preloadFormId = (objectId == null || objectId === -1) && config.preloadFormId || null
    files = config.files
    categoryKey = config.categoryKey
    url = config.url
    documentType = config.documentType
    property = config.property
  } else {
    objectId = arguments[0]
    preloadFormId = null
    files = arguments[1]
    categoryKey = arguments[2]
    url = arguments[3]
    documentType = arguments[4]
    property = arguments[5]
  }

  if (!files || files.length === 0) {
    return []
  }
  files = files.filter(file => file != null)
  if (files.length === 0) {
    return []
  }

  const fileWrappers = []
  for (let i = 0; i < files.length; i++) {
    let fileWrapper
    const fd = new FormData()
    const fileObject = files[i]
    if (fileObject instanceof File) {
      fileWrapper = { // TODO FileDocument
        url: url,
        formData: fd,
        file: fileObject
      }
    } else {
      fileWrapper = fileObject
      if (fileWrapper.type === 'folder') {
        const children = fileWrapper._node.children
        if (!children || !children.length) {
          fileWrapper.path = getFilePath(fileWrapper)
          if (property) { // TODO
            fileWrapper.property = property
          }
          fileWrappers.push(fileWrapper)
        }
        continue
      } else {
        fileWrapper = Object.assign(fileWrapper, {
          url: url,
          formData: fd
        })
      }
    }

    fd.append('id', objectId || -1)
    fd.append('file', fileWrapper.file)
    if (fileWrapper.originalTime) {
      fd.append('originalTime', fileWrapper.originalTime)
    }
    if (fileWrapper.originalHash) {
      fd.append('originalHash', fileWrapper.originalHash)
    }
    if (fileWrapper.attributes && typeof fileWrapper.attributes === 'object' && Object.keys(fileWrapper.attributes).length) {
      fd.append('attributes', JSON.stringify(fileWrapper.attributes))
    }
    if (fileWrapper.ignoreDuplicate === true) {
      fd.append('ignoreDuplicate', fileWrapper.ignoreDuplicate)
    }
    if (fileWrapper.formId) {
      fd.append('formId', fileWrapper.formId)
    } else if (preloadFormId) {
      fd.append('formId', preloadFormId)
    }

    if (categoryKey) {
      fd.append('category', categoryKey)
    }
    if (documentType) {
      fd.append('type', documentType)
    }
    if (property) {
      fd.append('property', property)
    }
    const folderPath = getFileFolderPath(fileWrapper, true)
    if (folderPath != null) {
      fd.append('folder', folderPath)
    }
    fileWrappers.push(fileWrapper)
  }

  return fileWrappers
}

export function getFilePath (fileWrapper) {
  const parentPath = getFileFolderPath(fileWrapper, true)
  if (parentPath) {
    return parentPath + '/' + fileWrapper.name
  }
  return fileWrapper.name
}

export function getFileFolderPath (fileWrapper, initial) {
  const parentFileWrapper = fileWrapper.parent
  if (parentFileWrapper) {
    if (parentFileWrapper.id !== -1) {
      return getFileFolderPath(parentFileWrapper) + parentFileWrapper.name + (initial ? '' : '/')
    } else if (parentFileWrapper.hierarchy) {
      return ''
    }
  }
  return initial ? null : ''
}

/**
 * @param filesData = [
 *    {
 *      file,
 *      url,
 *      formData
 *    }
 * ]
 */
export async function getUploadResult (filesData, notify, target, notifyAdditional, uploadConfig) {
  if (!filesData || filesData.length === 0) {
    return Promise.resolve()
  }

  const debugInfo = JSON.stringify({
    filesCount: filesData.length,
    actionTime: new Date().getTime()
  })
  for (const fileData of filesData) {
    fileData.formData.set('_debugInfo_', debugInfo)
  }

  const promises = await queueProcessingFilesData(filesData, UPLOAD_CONFIG.fileParallelism, uploadConfig)
  const results = await Promise.allSettled(promises)
  return prepareResult(results, notify, target, notifyAdditional, debugInfo)
}

/**
 * @param portionAsyncUploadLength
 *  Параметр, который определяет, количество файлов для синхронной загрузки
 */
async function queueProcessingFilesData (filesData, portionAsyncUploadLength, uploadConfig) {
  const promises = []
  let portionFilesData = []
  for (let i = 0; i < filesData.length; i++) {
    portionFilesData.push(filesData[i])

    if (portionFilesData.length === portionAsyncUploadLength || i === filesData.length - 1) {
      const portionPromises = []
      for (let j = 0; j < portionFilesData.length; j++) {
        const fileData = portionFilesData[j]
        await convertFileIfNeeded(fileData, uploadConfig)

        const promise = axiosPost({
          url: fileData.url,
          data: fileData.formData,
          config: { headers: { 'Content-Type': 'multipart/form-data' } }
        })
        if (fileData.callback || fileData.onSuccess || fileData.onFailure) {
          let callbackPromise = promise
          if (fileData.callback || fileData.onSuccess) {
            callbackPromise = callbackPromise
              .then(response => {
                try {
                  if (fileData.onSuccess) {
                    fileData.onSuccess(response)
                  }
                  if (fileData.callback) {
                    fileData.callback(response, null, fileData)
                  }
                } catch (err) {
                  console.log(err)
                }
                return response
              })
          }
          if (fileData.callback || fileData.onFailure) {
            callbackPromise = callbackPromise
              .catch(response => {
                try {
                  if (fileData.callback) {
                    fileData.callback(response, null, fileData)
                  }
                  if (fileData.onFailure) {
                    fileData.onFailure(response)
                  }
                } catch (err) {
                  console.log(err)
                }
                return Promise.reject(response)
              })
          }
        }
        portionPromises.push(promise)
      }

      await Promise.allSettled(portionPromises)
      portionPromises.forEach(promise => promises.push(promise))
      portionFilesData = []
    }
  }
  return promises
}

async function convertFileIfNeeded (fileData, uploadConfig) {
  // Если это фотография, при необходимости сжимаем или конверируем ее
  const fileName = fileData.name
  if (fileName) {
    const extensionIndex = fileName.lastIndexOf('.')
    if (extensionIndex !== -1 && extensionIndex !== fileName.length - 1) {
      const fileExtension = fileName.substring(extensionIndex).toLowerCase()
      if (UPLOAD_CONFIG.resizableImageExtensions.has(fileExtension) || UPLOAD_CONFIG.imageExtensionsToConvert.has(fileExtension)) {
        let quality = 0.95
        let maxImageWidth = UPLOAD_CONFIG.maxImageWidth
        let maxImageHeight = UPLOAD_CONFIG.maxImageHeight
        if (uploadConfig) {
          if (uploadConfig.maxSize != null) {
            maxImageWidth = uploadConfig.maxSize
            maxImageHeight = uploadConfig.maxSize
          }
          if (uploadConfig.quality != null) {
            quality = uploadConfig.quality
          }
        }

        await loadImage(fileData.file, {
          maxWidth: maxImageWidth,
          maxHeight: maxImageHeight,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
          canvas: true,
          meta: true,
          orientation: true
        })
          .then(function (data) {
            if (data.originalWidth > maxImageWidth || data.originalHeight > maxImageHeight || UPLOAD_CONFIG.imageExtensionsToConvert.has(fileExtension)) {
              return new Promise((resolve, reject) => {
                try {
                  const newExtension = 'jpeg'
                  const canvas = data.image
                  canvas.toBlob(async function (blob) {
                    const newFileName = fileName.substring(0, extensionIndex) + '.' + newExtension
                    let convertedFile = new File([blob], newFileName, {
                      type: 'image/' + newExtension,
                      lastModified: fileData.file.lastModified
                    })

                    try {
                      const exifObjectConverted = await getConvertedExif(fileData.file, data.exif)
                      if (exifObjectConverted) {
                        convertedFile = await writeFileExif(convertedFile, exifObjectConverted)
                      }
                    } catch (e) {
                      console.error('Ошибка формирования Exif', e)
                    }

                    fileData.formData.set('file', convertedFile)
                    resolve()
                  }, 'image/' + newExtension, quality)
                } catch (e) {
                  reject(e)
                }
              })
            }
          })
          .catch(function (err) {
            console.error(err)
          })
      }
    }
  }
}

async function getConvertedExif (file, exif) {
  if (!exif) {
    return null
  }
  // См.: https://github.com/blueimp/JavaScript-Load-Image#exif-parser
  // https://github.com/hMatoba/piexifjs
  // https://auth0.com/blog/read-edit-exif-metadata-in-photos-with-javascript/

  const exifObjectConverted = {}

  let originalTimeText = null
  // console.log(exif.getAll()) // Все теги Exif
  const exifIFD = exif.get('Exif')
  if (exifIFD) {
    const exifIFDObject = {}
    copyExifProperty(exifIFD, exifIFDObject, piexif.ExifIFD.DateTimeOriginal, 'DateTimeOriginal')
    copyExifProperty(exifIFD, exifIFDObject, piexif.ExifIFD.SubSecTimeOriginal, 'SubSecTimeOriginal')
    if (exifIFDObject[piexif.ExifIFD.DateTimeOriginal]) {
      originalTimeText = exifIFDObject[piexif.ExifIFD.DateTimeOriginal]
    }
    if (Object.keys(exifIFDObject).length !== 0) {
      exifObjectConverted.Exif = exifIFDObject
    }
  }

  const zerothObject = {}
  copyExifProperty(exif, zerothObject, piexif.ImageIFD.DateTime, 'DateTime')
  copyExifProperty(exif, zerothObject, piexif.ImageIFD.Make, 'Make')
  copyExifProperty(exif, zerothObject, piexif.ImageIFD.Model, 'Model')
  if (!originalTimeText && zerothObject.dateTime) {
    originalTimeText = zerothObject.dateTime
  }
  if (Object.keys(zerothObject).length !== 0) {
    exifObjectConverted['0th'] = zerothObject
  }

  try {
    if (exif.get('GPSInfo')) {
      const exifObj = await readFileExif(file)
      if (exifObj && exifObj.GPS) {
        // Проверяем, что не нули. Такое бывает, если фото пересылалось через мессенджеры
        if (!isZeroGPSArray(exifObj.GPS[piexif.GPSIFD.GPSLatitude]) || !isZeroGPSArray(exifObj.GPS[piexif.GPSIFD.GPSLongitude])) {
          exifObjectConverted.GPS = exifObj.GPS
        }
      }
    }
  } catch (e) {
    console.error('Ошибка чтения данных GPS из Exif', e)
  }

  if (Object.keys(exifObjectConverted).length !== 0) {
    return exifObjectConverted
  }
  return null
}

function isZeroGPSArray (value) {
  if (value && Array.isArray(value)) {
    let hasNotZeroValue = false
    for (const innerArray of value) {
      if (innerArray && Array.isArray(innerArray)) {
        for (const innerValue of innerArray) {
          if (innerValue) {
            hasNotZeroValue = true
            break
          }
        }
        if (hasNotZeroValue) {
          break
        }
      }
    }
    return !hasNotZeroValue
  }
  return true
}

function readFileExif (file) {
  return new Promise((resolve, reject) => {
    var reader = new FileReader()

    reader.onloadend = function (evt) {
      if (evt.target.readyState == FileReader.DONE) {
        try {
          const dataUrl = evt.target.result
          var exifObj = piexif.load(dataUrl)
          // Логирование Exif:
          // for (var ifd in exifObj) {
          //   if (ifd == 'thumbnail') {
          //     continue
          //   }
          //   console.log('-' + ifd)
          //   for (var tag in exifObj[ifd]) {
          //     console.log('  ' + piexif.TAGS[ifd][tag].name + ':' + exifObj[ifd][tag])
          //   }
          // }
          resolve(exifObj)
        } catch (e) {
          console.error('Ошибка разбора Exif через piexif', e)
          resolve(null)
        }
      } else {
        reject(new Error('Ошибка преобразования файла'))
      }
    }

    reader.onerror = function () {
      reject(new Error('Ошибка чтения файла для Exif'))
    }

    reader.readAsDataURL(file)
  })
}

function copyExifProperty (source, target, number, name) {
  const value = source.get(name)
  if (value) {
    target[number] = value
  }
}

function writeFileExif (file, exifObjectConverted) {
  return new Promise((resolve, reject) => {
    let exifStrConverted
    try {
      exifStrConverted = piexif.dump(exifObjectConverted)
    } catch (e) {
      console.error('Ошибка формирования Exif для записи', e)
      resolve(file)
      return
    }

    var reader = new FileReader()

    reader.onloadend = function (evt) {
      if (evt.target.readyState == FileReader.DONE) {
        try {
          const dataUrl = evt.target.result
          const dataUrlWithExif = piexif.insert(exifStrConverted, dataUrl)
          const fileWithExif = dataURLtoFile(dataUrlWithExif, file.name, file.lastModified)
          resolve(fileWithExif)
        } catch (e) {
          console.error('Ошибка записи Exif в файл', e)
          resolve(file)
        }
      } else {
        reject(new Error('Ошибка преобразования файла'))
      }
    }

    reader.onerror = function () {
      reject(new Error('Ошибка чтения файла для записи Exif'))
    }

    reader.readAsDataURL(file)
  })
}

function dataURLtoFile (dataurl, filename, lastModified) {
  var arr = dataurl.split(',')
  var mime = arr[0].match(/:(.*?);/)[1]
  var bstr = atob(arr[1])
  var n = bstr.length
  var u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime, lastModified: lastModified })
}

function prepareResult (results, notify, target, notifyAdditional, debugInfo) {
  let errorsCount = 0
  let doublesCount = 0
  const additionalMessages = []
  const debugMessages = []
  results.forEach((result, num) => {
    if (result.status === 'rejected') {
      const message = result.reason.message
      console.info(message || result.reason)
      if (result.reason.code === 'duplicate' || (message && (message.endsWith('уже содержится в списке') || message.endsWith('был импортирован ранее')))) { // TODO : if (result.reason.type === 'file_double') {
        doublesCount++
      } else {
        errorsCount++

        // if (result.reason.notify) {
        let debugMessage
        if (typeof result.reason === 'string') {
          debugMessage = result.reason
        } else {
          debugMessage = message || 'undefined error'

          const error = result.reason && result.reason._error || null
          if (error) {
            if (error.message) {
              debugMessage += ', errorMessage: ' + error.message
            }
            if (error.cause) {
              try {
                debugMessage += ', errorCause: ' + JSON.stringify(error.cause)
              } catch (e) {
              }
            }
            const response = error.response
            if (response) {
              if (response.status) {
                debugMessage += ', status: ' + response.status
              }
              if (response.statusText) {
                debugMessage += ', statusText: ' + response.statusText
              }
            }
            try {
              debugMessage += ', errorData: ' + JSON.stringify(error)
            } catch (e) {
            }
            // if (error.stack) {
            //   debugMessage += ', stack:' + JSON.stringify(error.stack)
            // }
          }
        }
        debugMessages.push(debugMessage)
        // }
      }
      if (message) {
        if (result.reason.notify || notifyAdditional) {
          additionalMessages.push(message)
        }
      }
    }
  })

  const message = (errorsCount > 0 ? 'Не удалось загрузить ' + errorsCount + ' файл(a/ов). ' : '') + //
    'Загружено файлов : ' + (results.length - errorsCount - doublesCount) + '.' +//
    (doublesCount > 0 ? ' Дублей: ' + doublesCount + '.' : '') + //
    (additionalMessages.length ? '\n' + additionalMessages.join('\n') : '') //

  const result = {
    message: message,
    status: errorsCount > 0 ? 'error' : doublesCount > 0 ? 'warning' : 'positive',
    results: results
  }

  if (notify && target) {
    target.$q.notify({
      type: result.status === 'error' ? 'negative' : result.status,
      message: result.message,
      progress: true,
      timeout: 8000
    })
  }

  if (debugMessages.length) {
    const bodyFormData = new FormData()
    bodyFormData.set('type', 'warning')
    bodyFormData.set('message', debugMessages.join(';\n'))
    if (debugInfo) {
      bodyFormData.set('_debugInfo_', debugInfo)
    }

    axiosPost({
      url: '/service/server/console',
      data: bodyFormData
    })
  }

  return errorsCount > 0 ? Promise.reject(result) : result
}

export function transformTree (tree, rootId) {
  if (rootId === undefined) {
    rootId = Math.min.apply(null, Object.keys(tree))
  }

  const rootDirectChildren = tree[rootId]

  if (rootDirectChildren) {
    const iterateChildren = function (parentObject) {
      const children = tree[parentObject.id]

      if (children) {
        parentObject.children = children
        parentObject.children.forEach((o) => iterateChildren(o))
      }
    }

    rootDirectChildren.forEach((o) => iterateChildren(o))
    return rootDirectChildren
  }

  return []
}

export function transformTreeToList (tree, rootId) {
  if (rootId === undefined) {
    rootId = Math.min.apply(null, Object.keys(tree))
  }

  const treeElements = []
  const rootDirectChildren = tree[rootId]

  if (rootDirectChildren) {
    const iterateChildren = function (object, parentId) {
      if (parentId) {
        object.parent = parentId
      }

      treeElements.push(object)
      const children = tree[object.id]

      if (children) {
        children.forEach((o) => iterateChildren(o, object.id))
      }
    }

    rootDirectChildren.forEach((o) => iterateChildren(o))
  }

  return treeElements
}

export function getTreeElements (tree, rootId) {
  if (rootId === undefined) {
    rootId = Math.min.apply(null, Object.keys(tree))
  }

  const elements = {}
  const rootDirectChildren = tree[rootId]

  if (rootDirectChildren) {
    const iterateChildren = function (parentObject) {
      const element = {}

      Object.assign(element, parentObject)
      elements[parentObject.id] = element

      const children = tree[parentObject.id]

      if (children) {
        children.forEach((o) => iterateChildren(o))
      }
    }

    rootDirectChildren.forEach((o) => iterateChildren(o))
  }

  return elements
}

export const ALARM_SEVERITIES_CONFIG = {
  info: {
    name: 'Информационное',
    value: 0,
    bgColor: 'white',
    textColor: 'black',
    mapIcon: '/delta/dist/info16.png',
    mdiIcon: 'mdi-alert-outline',
    mdiIconColor: 'grey'
  },
  warning: {
    name: 'Предупреждение',
    value: 1,
    bgColor: 'orange',
    textColor: 'black',
    mapIcon: '/delta/dist/warn16.png',
    mdiIcon: 'mdi-alert',
    mdiIconColor: 'orange'
  },
  critical: {
    name: 'Критическое',
    value: 2,
    bgColor: 'red',
    textColor: 'white',
    mapIcon: '/delta/dist/err16.png',
    mdiIcon: 'mdi-alert',
    mdiIconColor: 'red'
  },
  fatal: {
    name: 'Фатальное',
    value: 3,
    bgColor: 'black',
    textColor: 'white',
    mapIcon: '/delta/dist/fatal16.png',
    mdiIcon: 'mdi-alert',
    mdiIconColor: 'black'
  }
}

export function convertRequestPriority (requestPriority) {
  if (Number(requestPriority) <= 6) {
    return 'Низкий'
  } else if (Number(requestPriority) == 7) {
    return 'Низкий'
  } else if (Number(requestPriority) == 8) {
    return 'Низкий'
  } else if (Number(requestPriority) == 9) {
    return 'Средний'
  } else if (Number(requestPriority) == 10) {
    return 'Средний'
  } else if (Number(requestPriority) == 11) {
    return 'Средний'
  } else if (Number(requestPriority) == 12) {
    return 'Средний'
  } else if (Number(requestPriority) == 13) {
    return 'Средний'
  } else if (Number(requestPriority) == 14) {
    return 'Высокий'
  } else if (Number(requestPriority) == 15) {
    return 'Высокий'
  } else if (Number(requestPriority) == 16) {
    return 'Высокий'
  } else if (Number(requestPriority) == 17) {
    return 'Высокий'
  } else if (Number(requestPriority) == 18) {
    return 'Высокий'
  } else if (Number(requestPriority) == 19) {
    return 'Высокий'
  } else if (Number(requestPriority) == 20) {
    return 'Срочно'
  } else if (Number(requestPriority) == 21) {
    return 'Срочно'
  } else if (Number(requestPriority) == 22) {
    return 'Срочно'
  } else if (Number(requestPriority) == 23) {
    return 'Срочно'
  } else if (Number(requestPriority) == 24) {
    return 'Критический'
  } else if (Number(requestPriority) == 25) {
    return 'Критический'
  } else if (Number(requestPriority) == 26) {
    return 'Критический'
  } else if (Number(requestPriority) == 27) {
    return 'Критический'
  } else if (Number(requestPriority) == 28) {
    return 'Критический'
  } else {
    return requestPriority
  }
}

export function specialCharsDecode (text) {
  if (text) {
    return text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/>/g, '&gt;')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&#171;/g, '«')
      .replace(/&#187;/g, '»')
  } else {
    return text
  }
}

export function getByteNameOfSize (size) {
  const sizeStr = size.toString()
  let lastDigit = null
  let penultDigit = null
  if (sizeStr && sizeStr.length > 0) {
    lastDigit = +sizeStr[sizeStr.length - 1]
  }
  if (sizeStr && sizeStr.length > 1) {
    penultDigit = +sizeStr[sizeStr.length - 2]
  }

  if ((lastDigit === 2 || lastDigit === 3 || lastDigit === 4) && penultDigit !== 1) {
    return 'байта'
  } else {
    return 'байт'
  }
}

export function formatTrueFalse (value) {
  if (value === true) {
    return 'Да'
  } else if (value === false) {
    return 'Нет'
  } else if (value === null) {
    return 'Нет значения'
  } else {
    return value
  }
}

export function slavicPlural (num, form1, form234, form567890) {
  if (!num && num != 0) { // check num is null
    return ''
  }
  num %= 100
  if (!num && num != 0) { // check num is not a number
    return ''
  }
  if (num < 0) {
    num = -num
  }
  if (num >= 5 && num <= 20) {
    return form567890
  }
  num %= 10
  if (num === 1) {
    return form1
  }
  if (num >= 2 && num <= 4) {
    return form234
  }
  return form567890
}

export function slavicPluralWithNumber (num, form1, form234, form567890, formZero) {
  if (!num && num != 0) { // check num is null
    return ''
  }
  num *= 1
  if (num === 0 && formZero) {
    return formZero
  }
  if (num || num === 0) {
    return num + ' ' + slavicPlural(num, form1, form234, form567890)
  }
  return ''
}

export function transformTableColumnsFromServerFormat (rowsRaw) {
  if (rowsRaw && typeof rowsRaw === 'object' && !Array.isArray(rowsRaw)) {
    const rows = []
    const columnsKeys = Object.keys(rowsRaw)
    if (columnsKeys.length) {
      const size = rowsRaw[columnsKeys[0]].length
      for (let i = 0; i < size; i++) {
        const row = {}
        columnsKeys.forEach(columnKey => {
          row[columnKey] = rowsRaw[columnKey][i]
        })
        rows.push(row)
      }
    }
    return rows
  }
  return rowsRaw
}

export function downloadFile (url, onFinally, target, method = 'GET', body = null) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.responseType = 'blob'

    xhr.onload = () => {
      if (onFinally) {
        onFinally()
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (xhr.getResponseHeader('Export-Message')) {
          if (target) {
            target.$d_ask({
              header: 'Внимание!',
              warning: decodeURI(xhr.getResponseHeader('Export-Message')).replaceAll(/\s/g, '\n').replaceAll(/\+/g, ' '),
              buttons: {
                ok: 'Закрыть'
              }
            })
          }
        }
        let filename = ''
        const disposition = xhr.getResponseHeader('Content-Disposition')
        if (disposition) {
          filename = getContentDispositionFileName(disposition)
        }

        const elem = window.document.createElement('a')
        elem.className = 'download'
        elem.download = filename
        elem.target = '_self'
        elem.href = URL.createObjectURL(xhr.response)

        elem.click()
        resolve(xhr.response)
      } else {
        const fileReader = new FileReader()
        fileReader.readAsText(xhr.response)

        fileReader.onload = function (event) {
          if (target) {
            const response = JSON.parse(event.target.result)
            const message = response.message || 'Произошла ошибка при экспорте'
            target.$q.notify({
              type: 'negative',
              message: message,
              progress: true,
              timeout: 8000
            })
            reject(message)
          }
        }
      }
    }
    xhr.onerror = () => {
      if (target) {
        const message = 'Произошла ошибка при экспорте'
        target.$q.notify({
          type: 'negative',
          message: message,
          progress: true,
          timeout: 8000
        })
        reject(message)
      }
    }
    xhr.open(method, url, true)

    function getCookie (name) {
      const matches = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
      return matches ? decodeURIComponent(matches[1]) : undefined
    }
    const token = getCookie('XSRF-TOKEN')

    xhr.setRequestHeader('X-XSRF-TOKEN', token)
    xhr.send(body)
  })
}

export function getContentDispositionFileName (disposition) {
  const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\.]+)(?:; ?|$)/i
  const asciiFilenameRegex = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i

  let fileName = null
  if (utf8FilenameRegex.test(disposition)) {
    fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)[1])
  } else {
    const matches = asciiFilenameRegex.exec(disposition)
    if (matches != null && matches[2]) {
      fileName = decodeURIComponent(matches[2])
    }
  }
  return fileName
}

const requestBPStatesNamesByKey = {
  BS1: 'Заявка создана',
  BS2: 'Анализ заявки',
  BS3: 'Назначен исполнитель',
  BS4: 'Заявка принята к исполнению',
  BS5: 'Выполняются работы',
  BS6: 'Работы завершены',
  BS7: 'Готовность к сдаче работ',
  BS8: 'Работы приняты',
  BS9: 'Заявка открыта повторно',
  BS10: 'Требуется дополнительный выезд',
  BS11: 'Продолжение работ отложено',
  BS12: 'Заявка отклонена',
  BS13: 'Заявка отклонена исполнителем',
  BS14: 'Доработка объекта',
  BS15: 'Доработка отчёта',
  BS17: 'Заявка закрыта',
  BS18: 'Заявка отозвана',
  BS19: 'Заявка отложена',
  BS20: 'Заявка переведена',
  BS21: 'Заявка возвращена'
}

export function getRequestStateName (key, request) {
  if (key === 'BS10' && request && request._configuration) {
    const executableOnDeparture = !!(request._configuration && request._configuration.executableOnDeparture)
    if (!executableOnDeparture) {
      return 'Требуется продолжение работ'
    }
  }
  return requestBPStatesNamesByKey[key]
}

export function getWorkShiftIconName (workShiftKey) {
  // TO-DO: получать иконки с сервера
  switch (workShiftKey) {
    case 'brigadier.7-19':
      return 'mdi-numeric-1-circle-outline'
    case 'brigadier.8-20':
      return 'mdi-numeric-2-circle-outline'
    case 'brigadier.19-31':
      return 'mdi-numeric-1-circle'
    case 'brigadier.20-32':
      return 'mdi-numeric-2-circle'
    case 'brigadier.0-24':
      return 'mdi-panorama-fisheye'
    case 'brigadier.8-17':
      return 'mdi-calendar-range'
    case 'brigadier.9-17':
      return 'mdi-calendar-range'
    default:
      return 'mdi-calendar'
  }
}

export class TextSearcher {
  constructor (str) {
    this.str = str
    this.regExp = new RegExp(str.replaceAll(/[её]/ig, '[её]'), 'i')
    // this.collator = new Intl.Collator('ru', { sensitivity: 'base' })
  }

  match (text) {
    return text && this.regExp.test(text)
  }

  matchByChar (text) {
    if (!text || text.length < this.str.length) {
      return false
    }

    let i1 = 0
    let i2 = 0
    let startIndex = -1
    let c1
    let c2
    while (i1 < this.str.length && i2 < text.length) {
      c1 = this.str.charAt(i1)
      c2 = text.charAt(i2)
      // if (c2 === 'ё') {
      //   c2 === 'e'
      // }
      // if (c1 !== c2) {
      if (this.collator.compare(c1, c2) !== 0) {
        if (startIndex !== -1) {
          i2 = startIndex
        }
        startIndex = -1
        i1 = 0
      } else {
        if (i1 === 0) {
          startIndex = i2
        }
        i1++
      }
      i2++
    }
    return i1 === this.str.length
  }
}

export async function getEmployeeIdByUserName (userName) {
  if (!userName) {
    return null
  }
  return axiosGet({
    url: `/service/delta/employee/employeeIdByUserName?userName=${userName}`,
    onSuccess: (response) => {
      if (response && response.data && response.data.employeeId) {
        return response.data.employeeId
      } else {
        return Promise.reject(new Error(response.message))
      }
    },
    onFailure: (error) => {
      throw new Error(error.message || 'Не удалось получить идентификатор сотрудника по имени пользователя')
    }
  })
}
